import type React from "react";
import { List } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";
import {
  LocalShipping as TrucksIcon,
  People as UsersIcon,
  LocationCity as CitiesIcon,
  School as TutorialIcon,
  CloudUpload as DataImporterIcon,
  Sync as LogislyIcon,
} from "@mui/icons-material";
import TruckGroupsMenu from "./TruckGroupsMenu";
import StaticValuesMenu from "./StaticValuesMenu";
import LogislySpxOnCallMenu from "./LogislySpxOnCallMenu";
import type { TruckGroup } from "../../types/truckGroup";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface SidebarMenuProps {
  isAdmin: boolean;
  isAdminOrStaff: boolean;
  isSuperUser: boolean;
  truckGroups: TruckGroup[];
  userId?: string;
  loading: boolean;
  onNavigate?: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  isAdmin,
  isAdminOrStaff,
  isSuperUser,
  truckGroups,
  userId,
  loading,
  onNavigate,
}) => {
  const location = useLocation();
  const { t } = useTranslation();

  const menuItems = [
    {
      text: t("Trucks" as TranslationKeys),
      icon: <TrucksIcon />,
      path: "/trucks",
      isTrucks: true,
      testId: TestId.SIDEBAR.MENU_ITEM_TRUCKS,
    },
    isAdminOrStaff && {
      text: "Logisly SPX OnCall",
      icon: <LogislyIcon />,
      path: "/logisly-spx-oncall",
      isLogislySpxOnCall: true,
      testId: TestId.SIDEBAR.MENU_ITEM_LOGISLY_SPX_ONCALL,
    },
    isAdmin && {
      text: t("Users" as TranslationKeys),
      icon: <UsersIcon />,
      path: "/users",
      testId: TestId.SIDEBAR.MENU_ITEM_USERS,
    },
    {
      text: t("Static Values" as TranslationKeys),
      icon: <CitiesIcon />,
      path: "/locations",
      isStaticValues: true,
      testId: TestId.SIDEBAR.MENU_ITEM_STATIC_VALUES,
    },
    isAdminOrStaff && {
      text: t("Data Importer" as TranslationKeys),
      icon: <DataImporterIcon />,
      path: "/dev-tools/data-importer",
      testId: TestId.SIDEBAR.MENU_ITEM_DATA_IMPORTER,
    },
    isSuperUser && {
      text: t("Dev Tools" as TranslationKeys),
      icon: <DataImporterIcon />,
      path: "/dev-tools/data-importer",
      testId: TestId.SIDEBAR.MENU_ITEM_DEV_TOOLS,
    },
    {
      text: t("Tutorial" as TranslationKeys),
      icon: <TutorialIcon />,
      path: "/tutorial",
      testId: TestId.SIDEBAR.MENU_ITEM_TUTORIAL,
    },
  ].filter(
    (
      item,
    ): item is typeof item & {
      text: string;
      icon: React.ReactNode;
      path: string;
      isTrucks?: boolean;
      isStaticValues?: boolean;
      isLogislySpxOnCall?: boolean;
      testId: string;
    } => item !== false,
  );

  return (
    <List>
      {menuItems.map((item) =>
        item.isTrucks ? (
          <TruckGroupsMenu
            key={item.text}
            truckGroups={truckGroups}
            userId={userId}
            loading={loading}
            onNavigate={onNavigate}
          />
        ) : item.isStaticValues ? (
          <StaticValuesMenu key={item.text} onNavigate={onNavigate} />
        ) : item.isLogislySpxOnCall ? (
          <LogislySpxOnCallMenu key={item.text} onNavigate={onNavigate} />
        ) : (
          <SidebarMenuItem
            key={item.text}
            to={item.path}
            selected={location.pathname === item.path}
            icon={item.icon}
            text={item.text}
            onNavigate={onNavigate}
            data-testid={item.testId}
          />
        ),
      )}
    </List>
  );
};

export default SidebarMenu;
